<template>
  <!-- Trial gate (PIX-464). No close button / mask / Esc dismissal. The
       PIX-241 "Browse for now" escape into the free-browse tier is removed
       for now - the only exits are start the trial, subscribe, or log out. -->
  <a-modal
    :open="true"
    centered
    :width="520"
    :footer="null"
    :closable="false"
    :maskClosable="false"
    :keyboard="false"
  >
    <div class="text-center pt-2">
      <div
        class="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3A6BD8] to-[#1E3456] flex items-center justify-center mx-auto mb-4 text-white"
      >
        <CrownIcon class="size-7" />
      </div>
      <h3 class="text-xl font-bold text-[#0F1B2D] mb-1.5">
        <!-- US demo: was "Try everything free for 7 days" -->
        Get started for free
      </h3>
      <!-- US demo: was "Pro features are now part of a subscription - but you
           get a one-time <b>7-day free trial</b> of everything on your board.
           No card required." -->
      <p class="text-sm text-gray-500 mb-6">
        Pick your board to unlock free access. No card required.
      </p>
    </div>

    <p class="text-[11px] font-bold tracking-widest text-gray-400 mb-2">
      YOUR BOARD
    </p>
    <div class="grid grid-cols-2 gap-2 mb-5">
      <button
        v-for="board in store.boards"
        :key="board.uuid"
        type="button"
        class="h-11 rounded-xl border text-sm font-semibold transition-colors cursor-pointer"
        :class="
          !bundlePicked && pickedBoardUuid === board.uuid
            ? 'border-[#5B8DEF] bg-[#F0F5FF] text-[#2563EB]'
            : 'border-gray-200 text-[#1E3456] hover:border-gray-300'
        "
        @click="pickBoard(board.uuid)"
      >
        {{ board.name }}
      </button>
      <button
        type="button"
        class="h-11 rounded-xl border text-sm font-semibold transition-colors cursor-pointer flex items-center justify-center gap-1.5 col-span-2"
        :class="
          bundlePicked
            ? 'border-[#5B8DEF] bg-[#F0F5FF] text-[#2563EB]'
            : 'border-gray-200 text-[#1E3456] hover:border-gray-300'
        "
        @click="pickBundle"
      >
        <LayersIcon class="size-4" />
        All Board Bundle
      </button>
    </div>

    <div
      v-if="errorMessage"
      class="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
    >
      {{ errorMessage }}
    </div>

    <div class="space-y-2.5">
      <Button
        variant="blue"
        block
        size="large"
        showShadow
        :loading="isStarting"
        :disabled="!hasPick"
        class="h-[46px] text-sm tracking-wide"
        @click="startTrial"
      >
        <!-- US demo: was "Start 7-Day Free Trial" -->
        <span>Continue for free</span>
        <ArrowRightIcon class="ml-3 size-4" />
      </Button>
      <Button
        variant="white"
        block
        size="large"
        :disabled="isStarting || !hasPick"
        class="h-[46px] text-sm tracking-wide border border-gray-200"
        @click="subscribeNow"
      >
        <CreditCardIcon class="mr-3 size-4" />
        <span>Subscribe now</span>
      </Button>
      <!-- US demo: was "7-day free trial · No card required" -->
      <p class="text-xs text-gray-400 text-center pt-1">
        Free plan · No card required · Upgrade anytime
      </p>
      <p class="text-center pt-1">
        <button
          type="button"
          class="text-xs text-gray-400 hover:text-gray-600 underline cursor-pointer"
          :disabled="isStarting"
          @click="logOut"
        >
          Log out
        </button>
      </p>
    </div>
  </a-modal>
</template>

<script setup>
import {
  ArrowRightIcon,
  CreditCardIcon,
  CrownIcon,
  LayersIcon,
} from 'lucide-vue-next';
import Button from '@/components/Button.vue';
import { get, post } from '@/api';
import { useActiveBoardStore } from '@/stores/activeBoard';
import { useAuthStore } from '@/stores/auth';
import { useSubscriptionStore } from '@/stores/subscription';
import { analytics } from '@/observability/analytics';
import { EVENTS, PROPS, TRIAL_START_PATH } from '@/observability/events';

const router = useRouter();
const activeBoardStore = useActiveBoardStore();
const authStore = useAuthStore();
const store = useSubscriptionStore();

const pickedBoardUuid = ref(null);
const bundlePicked = ref(false);
const isStarting = ref(false);
const errorMessage = ref('');

const hasPick = computed(
  () => bundlePicked.value || Boolean(pickedBoardUuid.value)
);

const pickBoard = (uuid) => {
  pickedBoardUuid.value = uuid;
  bundlePicked.value = false;
};
const pickBundle = () => {
  bundlePicked.value = true;
  pickedBoardUuid.value = null;
};

const startTrial = async () => {
  if (!hasPick.value) return;
  errorMessage.value = '';
  isStarting.value = true;
  try {
    const { status, data } = await post('api/subscriptions/start-trial/', {
      ...(bundlePicked.value
        ? { bundle: true }
        : { board_uuid: pickedBoardUuid.value }),
    });
    if (status === 201) {
      // The second route into a trial. Tagged distinctly because these are
      // pre-existing accounts with no landing visit in front of them -
      // pooling them with signup trials would distort both.
      analytics.track(EVENTS.TRIAL_STARTED, {
        [PROPS.TRIAL_START_PATH]: TRIAL_START_PATH.LEGACY_GATE,
        [PROPS.BOARD_SELECTION]: bundlePicked.value
          ? null
          : pickedBoardUuid.value,
        [PROPS.IS_BUNDLE]: bundlePicked.value,
      });

      // The board catalogue may have been loaded while this legacy account
      // was still a free browser, when the API intentionally returned every
      // board. Re-fetch it before refreshing auth (which unmounts this gate)
      // so the dashboard immediately reflects the new trial entitlement.
      await activeBoardStore.fetchAndHydrate(get);
      await authStore.fetchUser({ force: true });
      // US demo: was 'Your 7-day free trial has started - enjoy!'
      message.success("You're all set - enjoy!");
    } else {
      errorMessage.value =
        data?.detail || 'Could not start your trial. Try again.';
    }
  } catch {
    errorMessage.value = 'Could not start your trial. Try again.';
  } finally {
    isStarting.value = false;
  }
};

const subscribeNow = () => {
  if (!hasPick.value) return;
  // The checkout route is exempt from the gate; leaving it without paying
  // re-opens the gate on the next navigation (server-driven eligibility).
  router.push({
    name: 'subscription-checkout',
    query: bundlePicked.value
      ? { bundle: 'true' }
      : { board: pickedBoardUuid.value },
  });
};

const logOut = () => {
  authStore.logoutUser(() => {
    router.push({ name: 'login' });
  });
};

onMounted(async () => {
  if (!store.pricingTiers.length) await store.fetchCatalog();
  // Pre-set from the board(s) their existing subjects imply.
  const derived = authStore.derivedBoardUuids;
  const known = store.boards.find((b) => derived.includes(b.uuid));
  if (derived.length > 1) {
    bundlePicked.value = true;
  } else if (known) {
    pickedBoardUuid.value = known.uuid;
  } else if (store.boards.length) {
    pickedBoardUuid.value = store.boards[0].uuid;
  }
});
</script>
